import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Sparkles, MessageCircle, Map, Bus, ArrowRight } from 'lucide-react';
import { BookingWidget } from '../BookingWidget';
import { PlanMyTripButton } from '../TripPlannerQuiz';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default cinematic slow pan images for Ken Burns effect - optimized WebP
const defaultHeroImages = [
  '/images/hero-mountain-viewpoint.webp', // Mountain viewpoint with waterfalls
  '/images/hero-railway-bridge.webp',     // Scenic railway bridge in misty hills
  '/images/hero-deomali-valley.webp',     // Deomali valley with flower fields
];

const defaultHeroContent = {
  overline: 'The Jewel of the Eastern Ghats',
  title: 'Experience Emerald Serenity',
  subtitle: 'Nestled amidst the majestic hills of Koraput, The Hotel Emerald invites you to a world where luxury meets the untouched beauty of the Eastern Ghats.',
  buttons: [
    { text: 'Book Your Stay', link: '/book', style: 'primary' },
    { text: 'Explore Koraput', link: '/explore', style: 'secondary' }
  ]
};

export function HeroSection() {
  const containerRef = useRef(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [heroContent, setHeroContent] = useState(defaultHeroContent);
  const [heroImages, setHeroImages] = useState(defaultHeroImages);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Fetch hero settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        const settings = response.data;
        
        if (settings?.hero) {
          // Use settings if available, otherwise keep defaults
          if (settings.hero.images && settings.hero.images.length > 0) {
            setHeroImages(settings.hero.images.filter(img => img && img.trim()));
          }
          
          setHeroContent({
            overline: settings.hero.overline || defaultHeroContent.overline,
            title: settings.hero.title || defaultHeroContent.title,
            subtitle: settings.hero.subtitle || defaultHeroContent.subtitle,
            buttons: settings.hero.buttons && settings.hero.buttons.length > 0 
              ? settings.hero.buttons 
              : defaultHeroContent.buttons
          });
        }
      } catch (error) {
        console.error('Failed to fetch hero settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  // Rotate through images every 12 seconds
  useEffect(() => {
    if (heroImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 12000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Parse title to highlight "Emerald" word
  const renderTitle = () => {
    const title = heroContent.title;
    if (title.toLowerCase().includes('emerald')) {
      const parts = title.split(/(emerald)/i);
      return parts.map((part, i) => 
        part.toLowerCase() === 'emerald' 
          ? <span key={i} className="text-emerald-300">{part}</span> 
          : part
      );
    }
    return title;
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen lg:h-screen overflow-hidden"
      data-testid="hero-section"
    >
      {/* Cinematic Ken Burns Effect Background */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((img, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: currentImage === index ? 1 : 0,
              scale: currentImage === index ? [1, 1.15] : 1,
              x: currentImage === index ? ['0%', '3%'] : '0%',
            }}
            transition={{ 
              opacity: { duration: 2 },
              scale: { duration: 20, ease: 'linear' },
              x: { duration: 20, ease: 'linear' },
            }}
            className="absolute inset-0"
          >
            <motion.div style={{ y }} className="w-full h-full">
              <img
                src={img}
                alt="Deomali Peak at Sunset"
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </motion.div>
          </motion.div>
        ))}
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/50 via-emerald-950/30 to-emerald-950/70 z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/40 to-transparent z-10" />
      </div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-20 flex flex-col justify-center items-center text-center px-4 pt-24 pb-[280px] sm:pb-[200px] lg:pb-[180px] min-h-screen lg:h-full"
      >
        <div className="max-w-4xl">
          {/* Emerald AI Prompt - Top placement for better mobile visibility */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="mb-5 sm:mb-6"
          >
            <a 
              href="/emerald-ai"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 hover:bg-white/20 transition-all group"
              data-testid="hero-emerald-ai-prompt"
            >
              <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white text-xs sm:text-sm font-medium">
                  Meet <span className="text-emerald-300">Emerald AI</span>
                </p>
                <p className="text-emerald-100/70 text-[10px] sm:text-xs flex items-center gap-1.5">
                  <MessageCircle className="w-3 h-3" />
                  <span>Chat</span>
                  <span className="text-emerald-300/50">•</span>
                  <Map className="w-3 h-3" />
                  <span>Plan Your Trip</span>
                </p>
              </div>
              <motion.div
                animate={{ x: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="text-emerald-300 ml-1"
              >
                →
              </motion.div>
            </a>
          </motion.div>

          {/* Lovely Sis Tours & Travels Highlight */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mb-5 sm:mb-6 flex justify-center"
          >
            <a
              href="/tours"
              className="group relative inline-flex items-center gap-3 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 bg-gradient-to-r from-gold/20 to-emerald-500/10 backdrop-blur-md border border-gold/40 hover:border-gold/70 transition-all overflow-hidden"
              data-testid="hero-lovely-sis-prompt"
            >
              <motion.span
                animate={{ x: ['-120%', '220%'] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
                className="absolute top-0 left-0 h-full w-16 bg-white/20 skew-x-12 blur-md"
              />
              <span className="relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-gold to-amber-500 shadow-lg">
                <Bus className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-950" />
              </span>
              <span className="relative text-left">
                <span className="block text-white text-xs sm:text-sm font-medium">
                  <span className="text-gold">New</span> · Luxury <span className="text-gold">Urbania</span> Tours
                </span>
                <span className="block text-emerald-100/70 text-[10px] sm:text-xs">
                  Lovely Sis Tour &amp; Travels · Explore Koraput in style
                </span>
              </span>
              <ArrowRight className="relative w-4 h-4 text-gold transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="overline text-gold mb-3 sm:mb-4 speakable-content"
          >
            {heroContent.overline}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-7xl text-white mb-4 sm:mb-6 leading-tight speakable-content"
          >
            {renderTitle()}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="text-sm sm:text-lg md:text-xl text-emerald-100/90 max-w-2xl mx-auto mb-6 sm:mb-8 speakable-content px-2"
          >
            {heroContent.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center"
          >
            {heroContent.buttons.map((btn, index) => (
              <a
                key={index}
                href={btn.link}
                className={`${btn.style === 'secondary' ? 'btn-gold' : 'btn-emerald'} inline-flex items-center justify-center gap-2 text-sm sm:text-base py-2.5 sm:py-3`}
                data-testid={`hero-btn-${index}`}
              >
                {btn.text}
              </a>
            ))}
            <PlanMyTripButton variant="hero" />
          </motion.div>
        </div>

        {/* Scroll Indicator - hidden on mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="hidden lg:block absolute bottom-40"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="flex flex-col items-center text-white/70"
          >
            <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Booking Widget */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-0 right-0 z-30 px-2 sm:px-4"
        id="booking"
      >
        <div className="container-custom">
          <BookingWidget />
        </div>
      </motion.div>

      {/* Gradient Overlay at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-background to-transparent z-10" />
    </section>
  );
}
